import jsPDF from "jspdf";

interface SlumpTestPDFData {
  reportId: string;
  batchId: string;
  testDate: string;
  testTime: string;
  slumpValue: number;
  temperature: number;
  density: number;
  grade: string;
  location: string;
  weather: string;
  customerName: string;
  projectName: string;
}

export function generateSlumpTestPDF(data: SlumpTestPDFData): Blob {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  // Colors
  const deepIndigo = [27, 42, 91]; // #1B2A5B
  const carbonBlack = [10, 15, 28]; // #0A0F1C
  const mineralGray = [113, 113, 130]; // #717182
  const successGreen = [15, 61, 46]; // #0F3D2E
  const failRed = [92, 17, 17]; // #5C1111

  // Calculate compliance
  const slumpCompliance = data.slumpValue >= 75 && data.slumpValue <= 100;
  const temperatureCompliance = data.temperature <= 32;
  const densityCompliance = data.density >= 2400 && data.density <= 2500;
  const overallCompliance = slumpCompliance && temperatureCompliance && densityCompliance;

  // ===== HEADER =====
  // Border and logo placeholders
  doc.setDrawColor(deepIndigo[0], deepIndigo[1], deepIndigo[2]);
  doc.setLineWidth(2);
  doc.line(10, 15, pageWidth - 10, 15);

  // Lab name
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(deepIndigo[0], deepIndigo[1], deepIndigo[2]);
  doc.text("TRACK CONCRETE QUALITY CONTROL LABORATORY", pageWidth / 2, 22, { align: "center" });

  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(mineralGray[0], mineralGray[1], mineralGray[2]);
  doc.text("NABL Accredited Lab | Certificate No: TC-5678 | ISO 9001:2015 Certified", pageWidth / 2, 27, { align: "center" });

  // Report title
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(carbonBlack[0], carbonBlack[1], carbonBlack[2]);
  doc.text("SLUMP TEST REPORT", pageWidth / 2, 37, { align: "center" });

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(mineralGray[0], mineralGray[1], mineralGray[2]);
  doc.text("As per IS 1199-1959 (Reaffirmed 2004)", pageWidth / 2, 43, { align: "center" });

  // Border under header
  doc.setDrawColor(deepIndigo[0], deepIndigo[1], deepIndigo[2]);
  doc.setLineWidth(1.5);
  doc.line(10, 48, pageWidth - 10, 48);

  // ===== REPORT ID AND COMPLIANCE STATUS =====
  let yPos = 58;
  
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(mineralGray[0], mineralGray[1], mineralGray[2]);
  doc.text("REPORT NO.", 15, yPos);
  
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(carbonBlack[0], carbonBlack[1], carbonBlack[2]);
  doc.text(data.reportId, 15, yPos + 6);

  // Compliance badge
  const badgeColor = overallCompliance ? successGreen : failRed;
  doc.setFillColor(badgeColor[0], badgeColor[1], badgeColor[2]);
  doc.roundedRect(pageWidth - 60, yPos - 4, 45, 10, 2, 2, "F");
  
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text(overallCompliance ? "✓ COMPLIANT" : "✕ NON-COMPLIANT", pageWidth - 37.5, yPos + 2.5, { align: "center" });

  // ===== PROJECT DETAILS SECTION =====
  yPos = 75;
  
  doc.setFillColor(249, 250, 251);
  doc.roundedRect(15, yPos, pageWidth - 30, 40, 3, 3, "F");
  
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.5);
  doc.roundedRect(15, yPos, pageWidth - 30, 40, 3, 3, "S");

  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(mineralGray[0], mineralGray[1], mineralGray[2]);
  doc.text("PROJECT DETAILS", 20, yPos + 7);

  // Project details grid
  const detailsLeft = [
    { label: "Customer Name", value: data.customerName },
    { label: "Mix Design ID", value: data.batchId },
    { label: "Concrete Grade", value: data.grade },
  ];

  const detailsRight = [
    { label: "Project Name", value: data.projectName },
    { label: "Sample ID", value: `SMP-${data.batchId.split('-').pop()}-F1` },
    { label: "W/C Ratio", value: "0.45" },
  ];

  let detailY = yPos + 15;
  doc.setFontSize(7);

  detailsLeft.forEach((detail, i) => {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(mineralGray[0], mineralGray[1], mineralGray[2]);
    doc.text(detail.label, 20, detailY);
    
    doc.setFont("helvetica", "bold");
    doc.setTextColor(carbonBlack[0], carbonBlack[1], carbonBlack[2]);
    doc.text(detail.value, 20, detailY + 4);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(mineralGray[0], mineralGray[1], mineralGray[2]);
    doc.text(detailsRight[i].label, pageWidth / 2 + 5, detailY);
    
    doc.setFont("helvetica", "bold");
    doc.setTextColor(carbonBlack[0], carbonBlack[1], carbonBlack[2]);
    doc.text(detailsRight[i].value, pageWidth / 2 + 5, detailY + 4);

    detailY += 10;
  });

  // ===== TEST INFORMATION SECTION =====
  yPos = 125;
  
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.5);
  doc.roundedRect(15, yPos, pageWidth - 30, 30, 3, 3, "S");

  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(mineralGray[0], mineralGray[1], mineralGray[2]);
  doc.text("TEST INFORMATION", 20, yPos + 7);

  const testInfo = [
    { label: "Date of Test", value: data.testDate },
    { label: "Test Location", value: data.location },
  ];

  const testInfoRight = [
    { label: "Time of Test", value: data.testTime },
    { label: "Weather Conditions", value: data.weather },
  ];

  detailY = yPos + 15;
  doc.setFontSize(7);

  testInfo.forEach((detail, i) => {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(mineralGray[0], mineralGray[1], mineralGray[2]);
    doc.text(detail.label, 20, detailY);
    
    doc.setFont("helvetica", "bold");
    doc.setTextColor(carbonBlack[0], carbonBlack[1], carbonBlack[2]);
    doc.text(detail.value, 20, detailY + 4);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(mineralGray[0], mineralGray[1], mineralGray[2]);
    doc.text(testInfoRight[i].label, pageWidth / 2 + 5, detailY);
    
    doc.setFont("helvetica", "bold");
    doc.setTextColor(carbonBlack[0], carbonBlack[1], carbonBlack[2]);
    doc.text(testInfoRight[i].value, pageWidth / 2 + 5, detailY + 4);

    detailY += 10;
  });

  // ===== TEST RESULTS SECTION =====
  yPos = 165;
  
  doc.setDrawColor(deepIndigo[0], deepIndigo[1], deepIndigo[2]);
  doc.setLineWidth(1);
  doc.line(15, yPos, pageWidth - 15, yPos);

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(carbonBlack[0], carbonBlack[1], carbonBlack[2]);
  doc.text("TEST RESULTS", 15, yPos + 8);

  // Slump Value
  yPos += 18;
  doc.setFillColor(249, 250, 251);
  doc.roundedRect(15, yPos, pageWidth - 30, 30, 3, 3, "F");

  const slumpBadgeColor = slumpCompliance ? successGreen : failRed;
  doc.setFillColor(slumpBadgeColor[0], slumpBadgeColor[1], slumpBadgeColor[2]);
  doc.roundedRect(pageWidth - 35, yPos + 5, 20, 6, 1.5, 1.5, "F");
  
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text(slumpCompliance ? "PASS" : "FAIL", pageWidth - 25, yPos + 9, { align: "center" });

  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(mineralGray[0], mineralGray[1], mineralGray[2]);
  doc.text("SLUMP VALUE", 20, yPos + 7);

  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(carbonBlack[0], carbonBlack[1], carbonBlack[2]);
  doc.text(`${data.slumpValue}`, 20, yPos + 18);

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(mineralGray[0], mineralGray[1], mineralGray[2]);
  doc.text("mm", 45, yPos + 18);

  doc.setFontSize(7);
  doc.text("Specified Range: 75 - 100 mm", 20, yPos + 25);
  doc.text(`Deviation: ${slumpCompliance ? "Within Limits" : "Out of Range"}`, 100, yPos + 25);

  // Temperature
  yPos += 35;
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.5);
  doc.roundedRect(15, yPos, pageWidth - 30, 25, 3, 3, "S");

  const tempBadgeColor = temperatureCompliance ? successGreen : failRed;
  doc.setFillColor(tempBadgeColor[0], tempBadgeColor[1], tempBadgeColor[2]);
  doc.roundedRect(pageWidth - 35, yPos + 5, 20, 6, 1.5, 1.5, "F");
  
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text(temperatureCompliance ? "PASS" : "FAIL", pageWidth - 25, yPos + 9, { align: "center" });

  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(mineralGray[0], mineralGray[1], mineralGray[2]);
  doc.text("CONCRETE TEMPERATURE", 20, yPos + 7);

  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(carbonBlack[0], carbonBlack[1], carbonBlack[2]);
  doc.text(`${data.temperature}`, 20, yPos + 17);

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(mineralGray[0], mineralGray[1], mineralGray[2]);
  doc.text("°C", 40, yPos + 17);

  doc.setFontSize(7);
  doc.text("Maximum Limit: ≤ 32°C", 100, yPos + 17);

  // Density
  yPos += 30;
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.5);
  doc.roundedRect(15, yPos, pageWidth - 30, 25, 3, 3, "S");

  const densityBadgeColor = densityCompliance ? successGreen : failRed;
  doc.setFillColor(densityBadgeColor[0], densityBadgeColor[1], densityBadgeColor[2]);
  doc.roundedRect(pageWidth - 35, yPos + 5, 20, 6, 1.5, 1.5, "F");
  
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text(densityCompliance ? "PASS" : "FAIL", pageWidth - 25, yPos + 9, { align: "center" });

  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(mineralGray[0], mineralGray[1], mineralGray[2]);
  doc.text("FRESH CONCRETE DENSITY", 20, yPos + 7);

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(carbonBlack[0], carbonBlack[1], carbonBlack[2]);
  doc.text(`${data.density}`, 20, yPos + 17);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(mineralGray[0], mineralGray[1], mineralGray[2]);
  doc.text("kg/m³", 45, yPos + 17);

  doc.setFontSize(7);
  doc.text("Specified Range: 2400 - 2500 kg/m³", 100, yPos + 17);

  // ===== REMARKS =====
  yPos = pageHeight - 50;
  
  doc.setFillColor(254, 243, 199);
  doc.roundedRect(15, yPos, pageWidth - 30, 15, 3, 3, "F");

  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(120, 53, 15);
  doc.text("REMARKS", 20, yPos + 5);

  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  const remarks = overallCompliance
    ? "The fresh concrete sample has passed all slump test requirements. The concrete is suitable for construction use as per specifications."
    : "The fresh concrete sample has failed one or more test requirements. Please review the deviations and take corrective action before use.";
  
  const remarksLines = doc.splitTextToSize(remarks, pageWidth - 40);
  doc.text(remarksLines, 20, yPos + 10);

  // ===== FOOTER =====
  yPos = pageHeight - 15;
  
  doc.setFontSize(6);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(mineralGray[0], mineralGray[1], mineralGray[2]);
  doc.text("This is a computer-generated report. Authorized signatures are maintained digitally.", pageWidth / 2, yPos, { align: "center" });
  doc.text(`Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, pageWidth / 2, yPos + 4, { align: "center" });

  // Return blob instead of saving
  return doc.output('blob');
}

export function downloadSlumpTestPDF(data: SlumpTestPDFData): void {
  const blob = generateSlumpTestPDF(data);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${data.reportId}_SlumpTestReport.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
